import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ChevronDown,
  HelpCircle,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";

import { getAccessToken } from "../../../../awsAuth";
import "./FAQ.scss";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

const READONLY_PREVIEW_LIMIT = 5;

function makeFaqId() {
  return `faq_${Date.now()}_${Math.random()
    .toString(16)
    .slice(2)}`;
}

function normalizeFaq(items) {
  return (Array.isArray(items) ? items : [])
    .map((item) => ({
      id: String(item?.id || makeFaqId()),
      q: String(
        item?.q || item?.question || ""
      ).trim(),
      a: String(
        item?.a || item?.answer || ""
      ).trim(),
      createdAt:
        item?.createdAt ||
        new Date().toISOString(),
      updatedAt:
        item?.updatedAt ||
        new Date().toISOString(),
    }))
    .filter((item) => item.q || item.a);
}

export default function FAQ({
  ownerId,
  readonly = false,
}) {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);

  const [addingFaq, setAddingFaq] =
    useState(false);

  const [faqDraft, setFaqDraft] = useState({
    q: "",
    a: "",
  });

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const faqQRef = useRef(null);

  const endpoint = useMemo(() => {
    if (readonly && ownerId) {
      return `${API_BASE}/api/business/faq/user/${encodeURIComponent(
        ownerId
      )}`;
    }

    return `${API_BASE}/api/business/faq/me`;
  }, [readonly, ownerId]);

  const visibleItems = useMemo(() => {
    if (!readonly) return items;

    return items.slice(
      0,
      READONLY_PREVIEW_LIMIT
    );
  }, [items, readonly]);

  const hiddenCount = Math.max(
    0,
    items.length - visibleItems.length
  );

  const saveFaqItems = async (
    nextItems
  ) => {
    if (readonly) return;

    setSaving(true);

    try {
      const token =
        await getAccessToken();

      const res = await fetch(
        `${API_BASE}/api/business/faq/me`,
        {
          method: "PUT",
          headers: {
            "Content-Type":
              "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            items:
              normalizeFaq(nextItems),
          }),
        }
      );

      const data = await res
        .json()
        .catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.error ||
            "Не вдалося зберегти FAQ"
        );
      }

      setItems(
        normalizeFaq(
          data?.items || nextItems
        )
      );
    } catch (err) {
      console.error(
        "[FAQ] save error:",
        err
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    let alive = true;

    async function loadFaq() {
      try {
        setLoading(true);

        const token =
          await getAccessToken().catch(
            () => null
          );

        const headers = token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {};

        const res = await fetch(
          endpoint,
          { headers }
        );

        const data = await res
          .json()
          .catch(() => ({}));

        if (!alive) return;

        if (!res.ok) {
          setItems([]);
          return;
        }

        const nextItems = normalizeFaq(
          data?.items || []
        );

        setItems(nextItems);

        if (
          readonly &&
          nextItems.length > 0
        ) {
          setOpenId(null);
        }
      } catch (err) {
        console.error(
          "[FAQ] load error:",
          err
        );

        if (alive) {
          setItems([]);
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    loadFaq();

    return () => {
      alive = false;
    };
  }, [endpoint, readonly]);

  const toggleFaq = (id) => {
    setOpenId((current) =>
      current === id ? null : id
    );
  };

  const startAddFaq = () => {
    if (readonly) return;

    setAddingFaq(true);

    setFaqDraft({
      q: "",
      a: "",
    });

    requestAnimationFrame(() => {
      faqQRef.current?.focus?.();
    });
  };

  const cancelAddFaq = () => {
    setAddingFaq(false);

    setFaqDraft({
      q: "",
      a: "",
    });
  };

  const saveFaqDraft = async () => {
    if (readonly) return;

    const q = String(
      faqDraft.q || ""
    ).trim();

    const a = String(
      faqDraft.a || ""
    ).trim();

    if (!q && !a) return;

    const nextItem = {
      id: makeFaqId(),
      q,
      a,
      createdAt:
        new Date().toISOString(),
      updatedAt:
        new Date().toISOString(),
    };

    const nextItems = [
      ...items,
      nextItem,
    ];

    setItems(nextItems);
    setAddingFaq(false);

    setFaqDraft({
      q: "",
      a: "",
    });

    await saveFaqItems(nextItems);
  };

  const updateFaq = async (
    id,
    patch
  ) => {
    if (readonly) return;

    const nextItems = items.map(
      (item) => {
        if (item.id !== id) {
          return item;
        }

        return {
          ...item,
          ...patch,
          q:
            typeof patch.q ===
            "string"
              ? patch.q
              : item.q,
          a:
            typeof patch.a ===
            "string"
              ? patch.a
              : item.a,
          updatedAt:
            new Date().toISOString(),
        };
      }
    );

    setItems(nextItems);
    await saveFaqItems(nextItems);
  };

  const removeFaq = async (id) => {
    if (readonly) return;

    const nextItems =
      items.filter(
        (item) => item.id !== id
      );

    setItems(nextItems);

    if (openId === id) {
      setOpenId(null);
    }

    await saveFaqItems(nextItems);
  };

  if (loading) {
    return (
      <div className="bpFaq">
        <div className="bpFaq__state">
          <span className="bpFaq__stateIcon">
            <HelpCircle
              size={22}
              strokeWidth={2.2}
            />
          </span>

          Завантаження FAQ...
        </div>
      </div>
    );
  }

  if (readonly) {
    if (!items.length) {
      return null;
    }

    return (
      <div className="bpFaq bpFaq--readonly">
        <div className="bpFaq__readonlyHead">
          <div className="bpFaq__readonlyIcon">
            <HelpCircle
              size={22}
              strokeWidth={2.2}
            />
          </div>

          <div>
            <strong>
              Часті запитання
            </strong>

            <span>
              {items.length}{" "}
              {items.length === 1
                ? "відповідь"
                : "відповідей"}
            </span>
          </div>
        </div>

        <div className="bpFaqAccordion">
          {visibleItems.map(
            (item, index) => {
              const isOpen =
                openId === item.id;

              return (
                <article
                  key={item.id}
                  className={[
                    "bpFaqAccordion__item",
                    isOpen
                      ? "is-open"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <button
                    type="button"
                    className="bpFaqAccordion__trigger"
                    onClick={() =>
                      toggleFaq(item.id)
                    }
                    aria-expanded={
                      isOpen
                    }
                  >
                    <span className="bpFaqAccordion__number">
                      {String(
                        index + 1
                      ).padStart(2, "0")}
                    </span>

                    <strong>
                      {item.q ||
                        "Питання"}
                    </strong>

                    <span
                      className={[
                        "bpFaqAccordion__chevron",
                        isOpen
                          ? "is-open"
                          : "",
                      ].join(" ")}
                    >
                      <ChevronDown
                        size={18}
                        strokeWidth={2.35}
                      />
                    </span>
                  </button>

                  <div
                    className={[
                      "bpFaqAccordion__answerWrap",
                      isOpen
                        ? "is-open"
                        : "",
                    ].join(" ")}
                  >
                    <div className="bpFaqAccordion__answer">
                      <p>
                        {item.a ||
                          "Відповідь ще не додана."}
                      </p>
                    </div>
                  </div>
                </article>
              );
            }
          )}
        </div>

        {hiddenCount > 0 && (
          <div className="bpFaq__more">
            Ще {hiddenCount}{" "}
            {hiddenCount === 1
              ? "питання"
              : "питань"}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bpFaq bpFaq--editable">
      <div className="bpFaq__head">
        <div>
          <strong>
            Актуальні питання
          </strong>

          <span>
            Питання та відповіді,
            які часто питають клієнти
          </span>
        </div>

        {!addingFaq && (
          <button
            type="button"
            onClick={startAddFaq}
            disabled={saving}
          >
            <Plus
              size={18}
              strokeWidth={2.7}
            />

            Додати
          </button>
        )}
      </div>

      <div className="bpFaq__list">
        {items.map(
          (item, index) => (
            <div
              className="bpFaqRow"
              key={item.id}
            >
              <div className="bpFaqRow__number">
                {index + 1}
              </div>

              <div className="bpFaqRow__fields">
                <input
                  value={item.q}
                  onChange={(event) => {
                    const value =
                      event.target.value;

                    setItems((prev) =>
                      prev.map((row) =>
                        row.id === item.id
                          ? {
                              ...row,
                              q: value,
                            }
                          : row
                      )
                    );
                  }}
                  onBlur={(event) =>
                    updateFaq(item.id, {
                      q: event.target.value,
                    })
                  }
                  placeholder="Питання"
                  disabled={saving}
                />

                <textarea
                  value={item.a}
                  onChange={(event) => {
                    const value =
                      event.target.value;

                    setItems((prev) =>
                      prev.map((row) =>
                        row.id === item.id
                          ? {
                              ...row,
                              a: value,
                            }
                          : row
                      )
                    );
                  }}
                  onBlur={(event) =>
                    updateFaq(item.id, {
                      a: event.target.value,
                    })
                  }
                  placeholder="Відповідь"
                  disabled={saving}
                />
              </div>

              <button
                type="button"
                className="bpFaqRow__remove"
                onClick={() =>
                  removeFaq(item.id)
                }
                disabled={saving}
                aria-label="Видалити питання"
              >
                <Trash2
                  size={17}
                  strokeWidth={2.5}
                />
              </button>
            </div>
          )
        )}

        {addingFaq && (
          <div className="bpFaqRow bpFaqRow--draft">
            <div className="bpFaqRow__number">
              +
            </div>

            <div className="bpFaqRow__fields">
              <input
                ref={faqQRef}
                value={faqDraft.q}
                onChange={(event) =>
                  setFaqDraft(
                    (prev) => ({
                      ...prev,
                      q:
                        event.target
                          .value,
                    })
                  )
                }
                placeholder="Питання"
                disabled={saving}
              />

              <textarea
                value={faqDraft.a}
                onChange={(event) =>
                  setFaqDraft(
                    (prev) => ({
                      ...prev,
                      a:
                        event.target
                          .value,
                    })
                  )
                }
                placeholder="Відповідь"
                disabled={saving}
              />
            </div>

            <div className="bpFaqRow__draftActions">
              <button
                type="button"
                onClick={saveFaqDraft}
                disabled={saving}
                aria-label="Зберегти"
              >
                <Save
                  size={16}
                  strokeWidth={2.5}
                />
              </button>

              <button
                type="button"
                onClick={cancelAddFaq}
                disabled={saving}
                aria-label="Скасувати"
              >
                <X
                  size={16}
                  strokeWidth={2.5}
                />
              </button>
            </div>
          </div>
        )}
      </div>

      {!items.length &&
        !addingFaq && (
          <div className="bpFaq__empty">
            <span className="bpFaq__emptyIcon">
              <HelpCircle
                size={24}
                strokeWidth={2.2}
              />
            </span>

            <strong>
              FAQ порожній
            </strong>

            <span>
              Додай відповіді про ціну,
              запис, оплату, доставку або
              передоплату.
            </span>
          </div>
        )}
    </div>
  );
}